import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { api, errDetail, money, fmtDateTime } from "@/lib/api";

export function MembershipBox({ dash, cfg, onChange }) {
  const { t, lang, tErr } = useLang();
  const u = dash.user;
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const [params] = useSearchParams();
  useEffect(() => {
    if (params.get("buy") === "hours" && boxRef.current) {
      boxRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      boxRef.current.classList.add("ring-2", "ring-red-500");
    }
  }, [params]);
  const price = dash.pricing?.extra_hour_price_cad ?? 30;
  const active = u.membership_status === "active";
  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(tErr(errDetail(e))); } finally { setBusy(false); } };
  const renewal = (cancel) => run(async () => { await api.post("/payments/renewal", { cancel }); toast.success(cancel ? t("member.renewalOff") : t("member.renewalOn")); onChange(); });
  const buy = () => run(async () => {
    const { data } = await api.post("/payments/extra-hours", { hours: qty, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  });
  const portal = () => run(async () => { const { data } = await api.post("/payments/portal", { origin_url: window.location.origin }); window.location.href = data.url; });

  return (
    <div className="mb-10 grid gap-3 lg:grid-cols-3">
      {active && u.period_end && (
        <div className="panel p-5" data-testid="member-renewal-box">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{u.cancel_at_period_end ? t("member.accessUntil") : t("member.nextRenewal")}</p>
          <p className="mt-2 text-lg text-white" data-testid="member-renewal-date">{fmtDateTime(u.period_end, lang)}</p>
          <p className="mt-1 text-xs text-slate-500">{t("member.expireNote")}</p>
          {u.stripe_subscription_id && cfg?.stripe_connected && (
            <div className="mt-4 flex flex-wrap gap-2">
              {u.cancel_at_period_end
                ? <button disabled={busy} onClick={() => renewal(false)} data-testid="resume-renewal-btn" className="btn-red rounded-full px-4 py-2 text-sm font-semibold">{t("member.resume")}</button>
                : <button disabled={busy} onClick={() => renewal(true)} data-testid="cancel-renewal-btn" className="btn-chrome rounded-full px-4 py-2 text-sm">{t("member.cancelRenewal")}</button>}
              <button disabled={busy} onClick={portal} data-testid="manage-subscription-btn" className="btn-chrome rounded-full px-4 py-2 text-sm">{t("member.manage")}</button>
            </div>
          )}
        </div>
      )}
      {active && cfg?.stripe_connected && (
        <div ref={boxRef} id="extra-hours" className="panel p-5" data-testid="extra-hours-box">
          <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500">{t("member.extraHours")}</p>
          <p className="mt-2 text-white">{money(price, lang)} / h <span className="text-xs text-slate-500">+ {t("plans.taxShort")}</span></p>
          <div className="mt-4 flex items-center gap-2">
            <input type="number" min="1" max="20" value={qty} onChange={(e) => setQty(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
              className="h-10 w-20 rounded-lg border border-white/15 bg-black/40 px-3 text-white" data-testid="extra-hours-qty" />
            <button disabled={busy} onClick={buy} data-testid="extra-hours-buy-btn" className="btn-red rounded-full px-4 py-2 text-sm font-semibold">{t("member.buy")} · {money(price * qty, lang)}</button>
          </div>
        </div>
      )}
      <div className="panel p-5" data-testid="member-keycard-info">
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-500"><KeyRound size={14} className="text-red-500" />{t("member.keycard")}</p>
        <p className="mt-2 text-sm text-slate-300">{t("member.keycardNote")}</p>
        {u.role === "member" && <p className="mt-2 font-mono text-xs uppercase" data-testid="member-keycard-handover-status">
          {u.keycard_handover?.status === "confirmed" ? <span className="text-emerald-300">{lang === "fr" ? "Remise confirmée" : "Handover confirmed"}</span>
            : <span className="text-amber-300">{lang === "fr" ? "Remise de carte en attente" : "Keycard pickup pending"}</span>}</p>}
      </div>
    </div>
  );
}

export async function startCheckout(planId, opts = {}) {
  const { data } = await api.post("/payments/checkout", { plan_id: planId, origin_url: window.location.origin, ...opts });
  window.location.href = data.checkout_url;
}
